import {
  Component, Input, Output, EventEmitter,
  AfterViewInit, OnDestroy, ViewChild, ElementRef, inject
} from '@angular/core';
import { AutocompleteSpeechService } from '../Services/autocomplete-accessiblity.service';

@Component({
  selector: '[success-message]',
  standalone: true,
  host: {
    '[class.jobSavedStatus]': 'isFloating',
    '[class.baseColorPalette]': 'isFloating'
  },
  template: `
    <div class="newMsgContainer BorderSuccess" tabindex="-1" #container>
      <div class="iconContainer BkgSuccess">
        <i class="fa fa-check-circle" role="presentation"></i>
      </div>
      <div class="msgContainer">
        <h2 class="jobSavedMessage">{{ message }}</h2>
      </div>
      @if (!hideClose) {
        <a [id]="closeId"
           role="button"
           href="#0"
           [class.status-close]="isFloating"
           [class.success-Close]="!isFloating"
           [attr.aria-labelledby]="labelId"
           (click)="onClose($event)"
           (keydown.enter)="onClose($event)"
           (keydown.space)="onClose($event)"
           style="display: block;">
          <span [id]="labelId" class="screenReaderText">Close</span>
        </a>
      }
    </div>
  `
})
export class SuccessMessageDirective implements AfterViewInit, OnDestroy {
  @Input() message: string = '';
  @Input() type: string = '';
  /** Kept for attribute backward-compatibility; use (closed) output for close handling. */
  @Input() click: string = '';
  @Input() hideclosebutton: string = 'false';
  @Output() closed = new EventEmitter<void>();

  @ViewChild('container') private containerRef!: ElementRef<HTMLElement>;

  // Owned by the directive class — no appScope needed.
  // All active instances share this stack; pushing a new banner dismisses the previous one.
  private static readonly _stack: SuccessMessageDirective[] = [];

  private static _idCounter = 0;
  readonly uniqueId = ++SuccessMessageDirective._idCounter;
  readonly closeId = `anchorClose_${this.uniqueId}`;
  readonly labelId = `btnLbl_${this.uniqueId}`;

  private readonly speechService = inject(AutocompleteSpeechService);

  get isFloating(): boolean { return this.type === 'floating'; }
  get hideClose(): boolean { return this.hideclosebutton === 'true'; }

  ngAfterViewInit(): void {
    this.pushToStack();
    if (this.isFloating) {
      // Notify layout code to reposition floating stickers.
      // search.js (or any listener) can respond to this event without coupling.
      window.dispatchEvent(new CustomEvent('tg:adjustHeaderStickers'));
    }
    this.announceSuccess(this.message);
    this.containerRef?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.removeFromStack();
  }

  onClose(event: Event): void {
    event.preventDefault();
    this.removeFromStack();
    this.closed.emit();
  }

  private announceSuccess(msg: string): void {
    this.speechService.announce('');
    setTimeout(() => this.speechService.announce(msg), 100);
  }

  private pushToStack(): void {
    const previous = SuccessMessageDirective._stack[SuccessMessageDirective._stack.length - 1];
    SuccessMessageDirective._stack.push(this);
    if (previous && previous !== this) {
      // Dismiss the previous banner after the current change-detection cycle completes.
      setTimeout(() => previous.closed.emit());
    }
  }

  private removeFromStack(): void {
    const idx = SuccessMessageDirective._stack.indexOf(this);
    if (idx !== -1) {
      SuccessMessageDirective._stack.splice(idx, 1);
    }
  }
}
